import type { MonospaceWorkspace } from '#layers/data-model/public';

export const MAX_SWITCHER_WORKSPACES = 5;

export interface SwitcherWorkspace {
	workspace: MonospaceWorkspace;
	isCurrent: boolean;
}

export interface SwitcherWorkspaceSelection {
	workspaces: SwitcherWorkspace[];
	/** True when more workspaces are accessible than the capped list shows. */
	hasMore: boolean;
}

interface SelectSwitcherWorkspacesInput {
	current: MonospaceWorkspace | null | undefined;
	members: MonospaceWorkspace[];
	getName: (workspace: MonospaceWorkspace) => string;
}

/**
 * Ordering for the switcher list: the current workspace always leads (and is the only
 * checkmarked entry), the remaining member workspaces follow sorted by display name, and
 * the whole list is capped at {@link MAX_SWITCHER_WORKSPACES}. The current workspace is kept
 * even when it is absent from `members` so the active row is never dropped. `hasMore` reports
 * whether the cap hid any accessible workspaces.
 */
export function selectSwitcherWorkspaces({ current, members, getName }: SelectSwitcherWorkspacesInput): SwitcherWorkspaceSelection {
	const others = members
		.filter((workspace) => workspace.id !== current?.id)
		.toSorted((a, b) => getName(a).localeCompare(getName(b)));

	const ordered = current ? [current, ...others] : others;

	return {
		workspaces: ordered
			.slice(0, MAX_SWITCHER_WORKSPACES)
			.map((workspace) => ({ workspace, isCurrent: workspace.id === current?.id })),
		hasMore: ordered.length > MAX_SWITCHER_WORKSPACES,
	};
}
